import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchGalleryBySlug, getOptimizedImageUrl } from '../lib/cms';
import { Shield, Flame, Zap, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Kraftvaerker({ isEn }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGallery() {
      try {
        const data = await fetchGalleryBySlug('kraftvaerk');
        setGallery(data);
      } catch (error) {
        console.error('Error loading kraftvaerk gallery:', error);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, []);

  const lang = isEn ? 'en' : 'da';

  const seoTitle = isEn 
    ? 'Power Plant Insulation - Fredericia Isolerings-puder' 
    : 'Kraftværksisolering - Fredericia Isolerings-puder';
  const seoDesc = isEn 
    ? 'Specialized insulation solutions for CHP plants and district heating plants. Maximize energy efficiency and reduce heat loss.'
    : 'Specialiserede isoleringsløsninger til kraftvarmeværker og fjernvarmeværker. Maksimer energieffektiviteten og reducer varmetab.';

  const points = isEn ? [
    'Engineered for extreme temperatures and continuous operation conditions.',
    'Maximizes energy efficiency and reduces thermal loss significantly.',
    'Protects complex pipe bends, valves, and turbines with bespoke shapes.',
    'Ensures durability and reliability under tough operating environments.',
    'Easy to remove and reinstall during maintenance, cutting downtime.',
    '100% produced with green energy from our own solar plant.'
  ] : [
    'Konstrueret til ekstreme temperaturer og kontinuerlige driftsforhold.',
    'Maksimerer energieffektiviteten og reducerer varmetab markant.',
    'Beskytter komplekse rørbøjninger, ventiler og turbiner med skræddersyede former.',
    'Sikrer holdbarhed og pålidelighed under hårde driftsforhold.',
    'Nem at afmontere og genmontere ved vedligeholdelse, hvilket sparer tid.',
    '100% produceret med grøn strøm fra vores eget solcelleanlæg.'
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-brand-dark overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:5rem_5rem] opacity-10" />
        <div className="absolute right-0 top-0 h-full w-1/3 bg-brand-accent transform -skew-x-12 translate-x-12" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="w-full text-left space-y-4">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Power Plant Insulation' : 'Kraftværksisolering'}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content & Benefits */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 tracking-tight">
              {isEn ? 'Bespoke Solutions for Large Installations' : 'Specialløsninger til store installationer'}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
              {isEn 
                ? 'Insulation pads for CHP plants reduce heat loss and protect pipes and installations from extreme temperatures. They ensure efficiency and durability in tough operating conditions. Our district heating insulation reduces heat loss and protects pipes and installations from heat and damage - not least personal injury. It ensures efficiency and durability in demanding conditions.'
                : 'Isoleringspuder til kraftvarmeværker reducerer varmetab og beskytter rør og installationer mod ekstreme temperaturer. De sikrer effektivitet og holdbarhed under hårde driftsforhold. Vores fjernvarmeisolering reducerer varmetab og beskytter rør og installationer mod varme og skader – ikke mindst tilskadekomst af personer. Den sikrer effektivitet og holdbarhed under krævende forhold.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {points.map((point, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <Check className="h-5 w-5 text-black shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{point}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/kraftvarmevaerk.png', { width: 600 })}
                alt="Kraftværksisolering"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Dynamisk Galleri via CMS-reference */}
      <section className="py-20 bg-brand-dark border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Power Plant Installation Gallery' : 'Kraftværks installationsgalleri'}
            </h2>
            <p className="text-gray-400 text-sm mt-2">
              {isEn 
                ? 'Explore our high-quality power plant insulation installations. Automatically updated from our CMS.'
                : 'Udforsk vores kraftværks isoleringsinstallationer af høj kvalitet. Opdateres automatisk fra vores CMS.'}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : gallery?.images && gallery.images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {gallery.images.map((imgUrl, idx) => (
                <div 
                  key={idx} 
                  className="relative aspect-square rounded-lg overflow-hidden border border-gray-800 bg-brand-surface group hover:border-gray-700 transition-all duration-300"
                >
                  <img
                    src={getOptimizedImageUrl(imgUrl, { width: 400, height: 400, fit: 'cover' })}
                    alt={`Kraftværksisolering ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 font-mono text-xs">
              {isEn ? 'No gallery images found.' : 'Ingen galleribilleder fundet.'}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand-surface relative overflow-hidden">
        <div className="max-w-7xl mx-auto text-center px-4 sm:px-6 lg:px-8 space-y-6 relative z-10">
          <h2 className="text-3xl font-display font-bold text-white tracking-tight">
            {isEn ? 'Need a Specialized Solution?' : 'Har du brug for en specialløsning?'}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            {isEn 
              ? 'We design and produce insulation pads specifically for power plants and district heating systems. Contact us today for a free consultation.'
              : 'Vi designer og producerer isoleringspuder specifikt til kraftværker og fjernvarmesystemer. Kontakt os i dag for en uforpligtende samtale.'}
          </p>
          <div className="pt-2">
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              className="inline-flex items-center gap-2 bg-brand-accent hover:bg-brand-accent-hover text-white font-medium px-6 py-3.5 rounded transition-colors shadow-lg shadow-brand-accent/10"
            >
              {isEn ? 'Contact Our Team' : 'Kontakt vores team'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
